/**
 * types.js — turn recovered cell TEXT into values a spreadsheet can add up.
 *
 * WHY THIS IS THE POINT OF THE TOOL. Nobody converts a PDF to Excel in order to
 * look at it — they convert it to sum a column, sort by a date, or feed a
 * formula. Handing over a sheet where "10 000 ₽" is a STRING satisfies the
 * literal request and fails the actual one: the total comes out zero, the sort
 * puts "9" after "1 000", and the person retypes the table by hand, which is
 * what they came here to avoid.
 *
 * The .xlsx writer (access-export.js) already emits a numeric cell for a JS
 * number and a date-formatted cell for a Date. So the only missing piece was
 * ever this: deciding, per column, what the strings ARE.
 *
 * WHY PER COLUMN AND NOT PER CELL. A column of amounts with one "—" in it is a
 * column of amounts. A cell-by-cell decision would make that one row a string
 * inside a numeric column, which Excel reports as an inconsistency and which
 * breaks a SUM range in exactly the invisible way this file exists to prevent.
 * So a column commits as a whole, on a majority of its non-empty cells, and
 * cells that do not parse stay text inside it.
 *
 * WHY THE PARSER REFUSES THINGS IT COULD PARSE. "+7 999 123 45 67" is digits
 * and spaces; so is "1 234 567". Turning a phone number into 79991234567
 * destroys data and cannot be undone by the person looking at it, so the rules
 * below are deliberately narrow: thousands groups must be exactly three digits,
 * a leading + is a refusal, and anything with letters in it stays text. A
 * missed number costs one click on the column menu. A wrong one costs the file.
 *
 * Exposed as window.P2XTypes.
 */
(function (root) {
    'use strict';
    if (root.P2XTypes) return;

    /* NBSP, narrow NBSP and thin space all show up as thousands separators in
       PDFs produced by Word and by LaTeX respectively. */
    var SPACES = /[    ]/g;
    var CURRENCY = /^[\s$€£¥₽₴₸₺₹¢]+|[\s$€£¥₽₴₸₺₹¢]+$|\s?(руб\.?|р\.|USD|EUR|RUB|UAH|KZT|PLN|zł|kr)$/gi;

    /**
     * A number, or null.
     *
     * Grouping and decimal separators are ambiguous across locales ("1.234" is
     * one thousand two hundred in German and one-point-two-three-four in
     * English), so the rule is structural rather than locale-guessed: a
     * separator followed by exactly three digits, repeatedly, is grouping; a
     * single separator followed by one or two or more digits at the END is the
     * decimal point. "1.234" alone is therefore read as 1.234, which is what a
     * bare reading gives and what an English-language PDF means.
     */
    function toNumber(raw) {
        if (raw == null) return null;
        var s = String(raw).replace(SPACES, ' ').trim();
        if (!s) return null;

        // A phone number, a version, a range, a footnote marker.
        if (/^\+/.test(s)) return null;
        if (/[A-Za-zА-Яа-яЁё]/.test(s.replace(CURRENCY, ''))) return null;

        var neg = false;
        if (/^\(.*\)$/.test(s)) { neg = true; s = s.slice(1, -1).trim(); }   // accounting negatives
        s = s.replace(CURRENCY, '').trim();
        if (/%$/.test(s)) return null;      // a percentage is not the same number
        if (/^[-−–]/.test(s)) { neg = !neg; s = s.replace(/^[-−–]\s*/, ''); }
        if (!s) return null;

        // Grouped: 1 234 567,89 / 1,234,567.89 / 1.234.567,89
        var grouped = /^(\d{1,3})(([ .,])\d{3})+([.,]\d+)?$/.exec(s);
        var out;
        if (grouped) {
            var sep = grouped[3];
            var body = s;
            var tail = /[.,]\d+$/.exec(body);
            var frac = '';
            // The last separator is a decimal point only when it differs from
            // the grouping one; "1.234.567" has no fraction at all.
            if (tail && tail[0][0] !== sep) { frac = tail[0].slice(1); body = body.slice(0, -tail[0].length); }
            out = Number(body.replace(/[ .,]/g, '') + (frac ? '.' + frac : ''));
        } else if (/^\d+([.,]\d+)?$/.test(s)) {
            out = Number(s.replace(',', '.'));
        } else {
            return null;
        }
        if (!isFinite(out)) return null;
        return neg ? -out : out;
    }

    /**
     * A Date, or null. Four unambiguous shapes only.
     *
     * Day-first and month-first cannot be told apart from one value ("03.04" is
     * both), so a COLUMN decides: see inferColumn, which reads the whole column
     * and only commits to month-first when some value in it rules day-first out.
     */
    function dateParts(raw) {
        var s = String(raw == null ? '' : raw).replace(SPACES, ' ').trim();
        if (!s) return null;
        var m;
        // ISO: 2026-03-12
        if ((m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s))) {
            return { y: +m[1], a: +m[2], b: +m[3], iso: true };
        }
        // 12.03.2026 · 12/03/2026 · 12-03-2026 · 12.03.26
        if ((m = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/.exec(s))) {
            var y = +m[3];
            if (y < 100) y += y < 70 ? 2000 : 1900;
            return { y: y, a: +m[1], b: +m[2], iso: false };
        }
        return null;
    }

    function buildDate(p, monthFirst) {
        var d, mo;
        if (p.iso) { mo = p.a; d = p.b; }
        else if (monthFirst) { mo = p.a; d = p.b; }
        else { d = p.a; mo = p.b; }
        if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
        var out = new Date(Date.UTC(p.y, mo - 1, d));
        if (isNaN(out.getTime()) || out.getUTCMonth() !== mo - 1 || out.getUTCDate() !== d) return null;
        return out;
    }

    /**
     * inferColumn(values) → { type: 'number'|'date'|'text', monthFirst }
     *
     * `values` are the DATA cells of one column — the header is excluded by the
     * caller, because a header reading "2026" would otherwise vote for number in
     * a column of names.
     */
    function inferColumn(values) {
        var filled = [];
        for (var i = 0; i < values.length; i++) {
            var v = String(values[i] == null ? '' : values[i]).trim();
            if (v) filled.push(v);
        }
        if (filled.length < 2) return { type: 'text', monthFirst: false };

        var nums = 0, dates = 0, dayFirstImpossible = false, dayFirstPossible = false;
        for (var k = 0; k < filled.length; k++) {
            if (toNumber(filled[k]) !== null) nums++;
            var p = dateParts(filled[k]);
            if (p) {
                dates++;
                if (!p.iso) {
                    if (p.a > 12) dayFirstPossible = true;      // 25.03 can only be a day
                    if (p.b > 12) dayFirstImpossible = true;    // 03.25 can only be month-first
                }
            }
        }

        // Dates win ties: "12.03.2026" also parses as no number, but a column of
        // ISO dates would otherwise be nothing at all.
        var n = filled.length;
        if (dates / n >= 0.7) {
            return { type: 'date', monthFirst: dayFirstImpossible && !dayFirstPossible };
        }
        if (nums / n >= 0.7) return { type: 'number', monthFirst: false };
        return { type: 'text', monthFirst: false };
    }

    /** Cell text → the value the writer should receive, for a committed type. */
    function coerce(raw, type, monthFirst) {
        var s = String(raw == null ? '' : raw).trim();
        if (!s) return '';
        if (type === 'number') {
            var v = toNumber(s);
            return v === null ? s : v;
        }
        if (type === 'date') {
            var p = dateParts(s);
            if (!p) return s;
            var d = buildDate(p, monthFirst);
            return d || s;
        }
        return s;
    }

    /**
     * Apply a table's column types to its data rows.
     * `types` is one entry per column: 'auto' | 'text' | 'number' | 'date'.
     */
    function applyTypes(rows, types) {
        if (!rows.length) return { rows: [], resolved: [] };
        var head = rows[0] || [];
        var body = rows.slice(1);
        var cols = head.length;
        var resolved = [];

        for (var c = 0; c < cols; c++) {
            var chosen = (types && types[c]) || 'auto';
            var column = body.map(function (r) { return r[c]; });
            if (chosen === 'auto') {
                resolved.push(inferColumn(column));
            } else {
                var mf = chosen === 'date' ? inferColumn(column).monthFirst : false;
                resolved.push({ type: chosen, monthFirst: mf });
            }
        }

        var out = body.map(function (r) {
            var line = [];
            for (var c2 = 0; c2 < cols; c2++) {
                line.push(coerce(r[c2], resolved[c2].type, resolved[c2].monthFirst));
            }
            return line;
        });
        return { rows: out, resolved: resolved };
    }

    root.P2XTypes = {
        toNumber: toNumber,
        dateParts: dateParts,
        buildDate: buildDate,
        inferColumn: inferColumn,
        coerce: coerce,
        applyTypes: applyTypes,
    };
})(typeof self !== 'undefined' ? self : this);
